import url from 'url';
import Amplify, { Auth, Hub } from 'aws-amplify';
import { NavigationGuard } from 'vue-router';
import {
  _Boolean,
  GetBackendInfoQuery,
  GetBackendInfoQueryVariables,
} from '@/API';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import awsconfig from '@/aws-exports';
import Cognito from '@/cognito';
import { getBackendInfo } from '@/graphql/queries';
import serverlessExports from '@/serverless-exports';
import { commonModule } from '@/store/modules/CommonStore';
import { LANDSCAPE_PRODUCTION, PAGE_PATH_BY_NAME } from '@/utils/consts';

// https://github.com/aws-amplify/amplify-js/blob/aws-amplify%403.3.2/packages/auth/src/Auth.ts#L74
const OAUTH_FLOW_MS_TIMEOUT = 10 * 1000;
// This regex based on the HTML specifications.
// https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
// eslint-disable-next-line no-useless-escape
const emailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const getCurrentEnvironmentFromUrl = () => {
  if (location.hostname === 'localhost') {
    return ['olive', 'develop'];
  }
  const [subdomain] = location.hostname.split('.');
  const [tenant, landscape] = subdomain.split('-');
  return [tenant, landscape || LANDSCAPE_PRODUCTION];
};

const queryCognitoInfo = async (): Promise<void> => {
  const [tenant, landscape] = getCurrentEnvironmentFromUrl();
  const result = await graphqlQuery<
    GetBackendInfoQuery,
    GetBackendInfoQueryVariables
  >(getBackendInfo, { tenant, landscape });
  if (result && result.getBackendInfo) {
    commonModule.setCognitoInfo(result.getBackendInfo.cognitoInfo);
  }
};

enum Configured {
  COGNITO_INFO,
  UN_AUTH,
  REDIRECTED_FROM_IDP,
}

const isAuthenticated = async (
  cognitoInfo?: typeof commonModule.cognitoInfo
) => {
  if (
    !cognitoInfo ||
    !cognitoInfo.userPoolId ||
    !cognitoInfo.userPoolWebClientId
  ) {
    return false;
  }
  const cognito = new Cognito(cognitoInfo);
  try {
    await cognito.isAuthenticated();
    return true;
  } catch (error) {
    return false;
  }
};

const amplifyConfigure = async (
  cognitoInfo?: typeof commonModule.cognitoInfo
): Promise<Configured> => {
  const _isAuthenticated = await isAuthenticated(cognitoInfo);
  const redirectedFromIdp = location.hash.startsWith('#access_token');
  const isUnAuth =
    (!_isAuthenticated || !cognitoInfo || !cognitoInfo.amazonCognitoDomain) &&
    !redirectedFromIdp;
  awsconfig.aws_appsync_graphqlEndpoint = serverlessExports.graphqlEndpoint;
  if (isUnAuth) {
    Amplify.configure({
      ...awsconfig,
      Auth: {
        identityPoolId: serverlessExports.identityPoolId,
        region: awsconfig.aws_project_region,
      },
    });
    return Configured.UN_AUTH;
  }

  if (!cognitoInfo) {
    throw new Error(
      'Login failed in amplifyConfigure due to missing cognitoInfo'
    );
  }
  authenticatedConfigure(cognitoInfo);
  return redirectedFromIdp
    ? Configured.REDIRECTED_FROM_IDP
    : Configured.COGNITO_INFO;
};

const authenticatedConfigure = (
  cognitoInfo: NonNullable<typeof commonModule.cognitoInfo>
) => {
  const {
    amazonCognitoDomain,
    identityPoolId,
    region,
    userPoolId,
    userPoolWebClientId,
  } = cognitoInfo;
  const redirectUri = location.origin;
  awsconfig.aws_appsync_graphqlEndpoint = serverlessExports.graphqlEndpoint;
  Amplify.configure({
    ...awsconfig,
    Auth: {
      identityPoolId,
      region,
      userPoolId,
      userPoolWebClientId,
      oauth: {
        domain: `${amazonCognitoDomain}.auth.${region}.amazoncognito.com`,
        scope: ['email', 'profile'],
        redirectSignIn: redirectUri,
        redirectSignOut: redirectUri,
        responseType: 'token',
      },
    },
  });
};

const redirectIdentityProvider = (): void => {
  const cognitoInfo = commonModule.cognitoInfo;
  if (!cognitoInfo) {
    throw new Error(
      'Login failed in redirectIdentityProvider due to missing cognitoInfo'
    );
  }
  const {
    amazonCognitoDomain,
    identityProvider,
    region,
    userPoolWebClientId,
  } = cognitoInfo;
  const hostname = `${amazonCognitoDomain}.auth.${region}.amazoncognito.com`;
  const pathname = `/oauth2/authorize`;
  const cognitoURL = url.format({
    protocol: 'https',
    hostname,
    pathname,
    query: {
      response_type: 'token',
      client_id: userPoolWebClientId,
      redirect_uri: location.origin,
      identity_provider: identityProvider,
    },
  });
  location.assign(cognitoURL);
};

interface UserInfo {
  attributes: {
    'custom:loginName'?: string | null;
    sub?: string | null;
    identities: string;
  };
}

const setLoginInfoInStore = async () => {
  const userInfo: UserInfo = await Auth.currentUserInfo();
  const customLoginName = userInfo.attributes['custom:loginName'];
  const sub = userInfo.attributes.sub;
  const [tenant, landscape] = getCurrentEnvironmentFromUrl();

  const loginNameFromIdp: string = customLoginName
    ? customLoginName
    : JSON.parse(userInfo.attributes.identities)[0].userId;

  const loginName = getLoginName(loginNameFromIdp, tenant);

  const userAnonymousId: string = sub ? sub : 'sub is null';

  commonModule.setLoginName(loginName);
  commonModule.setUserAnonymousId(userAnonymousId);
  commonModule.setTenant(tenant);
  commonModule.setLandscape(landscape);
};

export const getLoginName = (loginNameFromIdp: string, tenant: string) => {
  if (tenant === 'takeda' && loginNameFromIdp.match(emailRegex)) {
    return loginNameFromIdp.split('@')[0] + '@tpc';
  }
  return loginNameFromIdp;
};

export const authenticate: NavigationGuard = async (to, _, next) => {
  const routerName: string = to.name ? to.name : '';
  if (routerName === 'maintenance') {
    next();
    return;
  }
  if (to.fullPath.includes('maintenance') && routerName === 'portal') {
    next(PAGE_PATH_BY_NAME.maintenance);
    return;
  }
  const configured = await amplifyConfigure(commonModule.cognitoInfo);
  switch (configured) {
    case Configured.UN_AUTH: {
      await queryCognitoInfo();
      commonModule.setRedirectPath(to.fullPath);
      redirectIdentityProvider();
      // todo cognito情報の取得に失敗した場合、どうするか
      break;
    }
    case Configured.REDIRECTED_FROM_IDP: {
      // Delay of a couple of 100ms until activated.
      await timeoutUntilAuthOrFail();
      location.assign(location.origin);
      break;
    }
    case Configured.COGNITO_INFO: {
      await setLoginInfoInStore();
      const redirectPath = commonModule.redirectPath;
      if (redirectPath) {
        commonModule.setRedirectPath(null);
        next(redirectPath);
      } else {
        next();
      }
      break;
    }
  }
};

export const authLogout = async () => {
  const cognitoInfo = commonModule.cognitoInfo;
  if (cognitoInfo && cognitoInfo.amazonCognitoDomain) {
    const cognito = new Cognito(cognitoInfo);
    cognito.logout();
  }
  await Auth.signOut();
};

// https://github.com/aws-amplify/amplify-js/blob/aws-amplify%403.3.2/packages/auth/src/Auth.ts#L1124
/* eslint-disable */
const timeoutUntilAuthOrFail = async () => {
  return new Promise<void>(res => {
    const timeoutId = setTimeout(() => {
      Hub.remove('auth', hostedUISignCallback);

      res();
    }, OAUTH_FLOW_MS_TIMEOUT);

    Hub.listen('auth', hostedUISignCallback);

    function hostedUISignCallback({ payload }: { payload: any }) {
      const { event } = payload;

      if (event === 'cognitoHostedUI' || event === 'cognitoHostedUI_failure') {
        clearTimeout(timeoutId);

        Hub.remove('auth', hostedUISignCallback);

        res();
      }
    }
  });
};
